import { useState, useEffect, useRef } from "react";
import { fetcher } from "../../../utils/fetcher";
import { getAllNotes } from "../../../api/notes/getAll";
import useAuthStore from "../../../store/authStore";
import "./CreateNewNoteModal.scss";
import "../../../components/ModalContent/ModalComponents.scss";

function CreateNewNoteModal({ onClose, onNoteCreated }) {
  const [title, setTitle] = useState("");
  const [noteGroups, setNoteGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showTitleError, setShowTitleError] = useState(false);

  const currentProject = useAuthStore((state) => state.currentProject);
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (currentProject?._id) {
      setIsLoading(true);
      getAllNotes(false, currentProject._id)
        .then((data) => {
          setNoteGroups(data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching note groups:", err);
          setNoteGroups([]);
          setIsLoading(false);
        });
    }
  }, [currentProject?._id]);

  const handleCreateNote = async () => {
    if (!title.trim()) {
      setShowTitleError(true);
      return;
    }
    if (!selectedGroup) return;

    setIsCreating(true);
    try {
      const newNote = await fetcher.post("/api/notes", {
        groupId: selectedGroup.id,
        title: title.trim(),
        content: "",
        color: selectedGroup.color,
        isActive: true,
      });

      if (onNoteCreated) {
        onNoteCreated(newNote, selectedGroup);
      }
    } catch (error) {
      console.error("Error creating note:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setTitle("");
    setSelectedGroup(null);
    setShowTitleError(false);
    onClose();
  };

  const isCreateDisabled = !title.trim() || !selectedGroup || isCreating;

  return (
    <div className="create-new-note-modal">
      <div className="modal-form">
        <div className="modal-form-title">Title</div>
        <input
          ref={inputRef}
          type="text"
          className="modal-text-input"
          placeholder="Note title..."
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (showTitleError && e.target.value.trim()) {
              setShowTitleError(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isCreateDisabled) {
              handleCreateNote();
            }
          }}
        />
        {showTitleError && (
          <div className="modal-helper-text">Please enter a title</div>
        )}

        <div className="modal-form-title">Note Group</div>
        <div className="notegroups-list">
          {isLoading ? (
            <div className="loading-state">Loading note groups...</div>
          ) : noteGroups.length === 0 ? (
            <div className="empty-state">No note groups found</div>
          ) : (
            noteGroups.map((group) => (
              <div
                key={group._id}
                className={`notegroup-item ${selectedGroup?._id === group._id ? "selected" : ""}`}
                onClick={() => setSelectedGroup(group)}
                style={{ "--notegroup-color": group.color }}
              >
                <div
                  className="notegroup-color-bar"
                  style={{ backgroundColor: group.color }}
                />
                <span className="notegroup-name">{group.name}</span>
                <span className="notegroup-count">
                  ({group.notes?.length || 0})
                </span>
                {selectedGroup?._id === group._id && (
                  <span className="material-symbols-outlined check-icon">
                    check
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        <div className="modal-form-buttons">
          <button
            className="modal-button cancel"
            onClick={handleCancel}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="modal-button save"
            onClick={handleCreateNote}
            disabled={isCreateDisabled}
          >
            {isCreating ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateNewNoteModal;
